import hoistNonReactStatics from 'hoist-non-react-statics';
import PropTypes from 'prop-types';
import React from 'react';

import { SCREEN_QUERY } from '../../components/constants';

export enum SCREEN_SIZE {
    SMALL = 'Small',
    MEDIUM = 'Medium',
    LARGE = 'Large',
}

export interface WithMediaQueryResolverProps {
    screenSize: SCREEN_SIZE;
}

export interface ScreenQuery {
    SMALL: string;
    MEDIUM: string;
    LARGE: string;
}

interface State {
    screenSize?: SCREEN_SIZE;
}

export default function withMediaQueryResolver<T extends WithMediaQueryResolverProps>(
    variablesToResolve: Array<string> = [],
    allVariables: Record<string, string> = {},
    screenQuery: ScreenQuery = SCREEN_QUERY
) {
    return (WrappedComponent: React.ComponentType<T>) => {
        class MediaQueryResolver extends React.Component<T, State> {
            static contextTypes = {
                withMedia: PropTypes.bool,
            };

            static displayName = `mediaQueryResolver(${WrappedComponent.displayName})`;

            constructor(props: T) {
                super(props);

                this.state = {};
            }

            componentDidMount() {
                this.small = window.matchMedia(screenQuery.SMALL);
                this.medium = window.matchMedia(screenQuery.MEDIUM);
                this.large = window.matchMedia(screenQuery.LARGE);

                this.handleSmallScreen(this.small);
                this.handleMediumScreen(this.medium);
                this.handleLargeScreen(this.large);

                this.small.addListener(this.handleSmallScreen);
                this.medium.addListener(this.handleMediumScreen);
                this.large.addListener(this.handleLargeScreen);
            }

            componentWillUnmount() {
                this.small.removeListener(this.handleSmallScreen);
                this.medium.removeListener(this.handleMediumScreen);
                this.large.removeListener(this.handleLargeScreen);
            }

            context!: React.ContextType<React.Context<{ withMedia: boolean }>>;

            small: MediaQueryList;
            medium: MediaQueryList;
            large: MediaQueryList;

            handleSmallScreen = (evt: MediaQueryList | MediaQueryListEvent) => {
                this.setScreenSizeIfMediaMatches(evt.matches, SCREEN_SIZE.SMALL);
            };

            handleMediumScreen = (evt: MediaQueryList | MediaQueryListEvent) => {
                this.setScreenSizeIfMediaMatches(evt.matches, SCREEN_SIZE.MEDIUM);
            };

            handleLargeScreen = (evt: MediaQueryList | MediaQueryListEvent) => {
                this.setScreenSizeIfMediaMatches(evt.matches, SCREEN_SIZE.LARGE);
            };

            setScreenSizeIfMediaMatches = (matches: boolean, screenSize: SCREEN_SIZE) => {
                if (matches) {
                    this.setState({ screenSize });
                }
            };

            render() {
                const { screenSize } = this.state;
                const { withMedia } = this.context;

                const shouldApplyMediaRules = screenSize !== SCREEN_SIZE.MEDIUM && withMedia;
                const screenSizePostfix = shouldApplyMediaRules ? screenSize : '';

                const computedVariables = variablesToResolve.reduce(
                    (acc, variableName) => ({
                        ...acc,
                        [variableName]: allVariables[`${variableName}${screenSizePostfix}`],
                    }),
                    {}
                );

                return <WrappedComponent {...this.props} {...computedVariables} screenSize={screenSize} />;
            }
        }

        return hoistNonReactStatics(MediaQueryResolver, WrappedComponent) as React.ComponentType<
            Omit<T, keyof WithMediaQueryResolverProps>
        >;
    };
}
